import store from 'app-components/utils/redux-store';
import { DateTime } from 'luxon';

import { accountsUtils } from 'app-components/flux/accounts';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { tagsSelectors } from 'app-components/flux/tags';
import { transactionsUtils } from 'app-components/flux/transactions';

import { numberUtils } from 'app-components/utils/number';

//
// given an immutable list of transactions, will return the currency symbol appropriate to represent
// the group.  Either the one shared currency, or "N/A" if they differ
//
// pass in an array of transaction arrays (support for multiple transaction groups), if you only have
// one transaction list, pass in [txList]
//
export function getSharedCurrency(txns, accounts) {
  let currency = null;
  if (txns && accounts && accounts.size > 0) {
    txns.forEach((txnList) => {
      txnList.forEach((x) => {
        const acctRec = accounts.get(x.accountId);
        if (acctRec) {
          const tc = acctRec.get('currency');
          if (tc !== currency) {
            currency = !currency ? tc : 'N/A';
          }
        }
      });
    });
  }
  return currency;
}

const pendingStates = [
  'QCS_REFRESH_BANK_PENDING',
  'QCS_BATCH_REFRESH_BANK_PENDING',
  'USER_OWNED_BANK_PENDING',
  'REAL_TIME',
  'USER_OWNED_REAL_TIME_BANK_PENDING',
];
export function isBankPendingTxn(txn) {
  return pendingStates.indexOf(txn.source) >= 0;
}

// -------------------------------------------------------
// txnHasTags
export function txnHasTags(txn) {
  return Boolean(txn.tags && (txn.tags.size > 0 || txn.tags.length > 0));
}

export function lookupTagName(tag) {
  const updatedTag = tagsSelectors.getTagById(store.getState(), tag.id) || tag;
  return updatedTag?.name;
}

export function txnTagString(txn) {
  const isSplit = transactionsUtils.isSplitTxn(txn);
  let str = null;
  if (isSplit) {
    txn.split.items.forEach((item) => {
      if (txnHasTags(item)) {
        item.tags.forEach((tag) => {
          const tagName = lookupTagName(tag);
          if (tagName) {
            str = str ? `${str}, ${tagName}` : tagName;
          }
        });
      }
    });
  } else if (txnHasTags(txn)) {
    txn.tags.forEach((tag) => {
      const tagName = lookupTagName(tag);
      if (tagName) {
        str = str ? `${str}, ${tagName}` : tagName;
      }
    });
  }
  return str;
}

export function commaSeperatedTagString(tags) {
  let str = null;
  if (tags && (tags.size > 0 || tags.length > 0)) {
    tags.forEach((tag) => {
      const tagName = lookupTagName(tag);
      if (tagName) {
        str = str ? `${str}, ${tagName}` : tagName;
      }
    });
  }
  return str;
}

// -------------------------------------------------------
export function isSupportedTransferTxn(txn, state) {
  if (transactionsUtils.isSplitTxn(txn)) {
    return Boolean(txn.split.items.find((item) => chartOfAccountsUtils.isSupportedTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isSupportedTransferCoa(txn.coa, state);
}

export function isBankPending(txn) {
  return isBankPendingTxn(txn);
}

export function isUserOwned(txn) {
  return /^USER_.+/.test(txn.source) || /^SCHEDULED_TRANSACTIO.+/.test(txn.source);
}

export function isBankOwnedPending(txn) {
  return txn.source && isBankPendingTxn(txn) && !isUserOwned(txn);
}

// check is SCHEDULED and BANK_PENDING txn
export function isUnAcceptedTxnType(txn) {
  return transactionsUtils.isUnacceptedScheduledTxn(txn) || isBankOwnedPending(txn);
}

// A "match" is one which has a user-owned txn matched to a posted-txn. This is specifically excluding a
// transactions that are user-owned and matched to bank-pending txn.
export function isMatchedToPostedTxn(txn) {
  return isUserOwned(txn) && txn.cpData && txn.cpData.id && txn.matchedTxn?.id;
}

export function isAssociatedWithBankPendingTxn(txn) {
  return txn.cpData && (txn.cpData.pendingId || txn.cpData.cpPendingId); // cpPendingId will become pendingId, backward compatible
}

export function areMatchable(txn1, txn2) {
  let srcTxn;
  let dstTxn;

  // console.log("ARE MATCHABLE ", txn1, txn2);

  if (isUserOwned(txn1)) {
    if (isUserOwned(txn2)) {
      return false; // one must be non user-owned
    }
    srcTxn = txn2;
    dstTxn = txn1;
  } else {
    if (!isUserOwned(txn2)) {
      return false; // one must be user-owned
    }
    srcTxn = txn1;
    dstTxn = txn2;
  }
  if (srcTxn.accountId !== dstTxn.accountId) {
    return false; // they must belong to the same account
  }
  if (isMatchedToPostedTxn(dstTxn)) {
    return false; // already matched to posted-txn (need to first unmatch before re-matching)
  }
  if (isBankPending(srcTxn) && isAssociatedWithBankPendingTxn(dstTxn)) {
    return false; // destination txn is already associated with a bank-pending txn
  }
  return true;
}

export function getTxnDifferences(txn, txnPrev) {
  const flds = [
    'accountId',
    'postedOn',
    'payee',
    'coa',
    'amount',
    'userFlag',
    'memo',
    'state',
    'isReviewed',
    'isDeleted',
    'tags',
    'check',
    'attachments',
    'split',
    'isExcludedFromReports',
    'isExcludedFromF2S',
  ];
  const changed = [];
  flds.forEach((fld) => {
    if (transactionsUtils.fieldChanged(txn, txnPrev, fld)) {
      changed.push(fld);
    }
  });
  return changed;
}

function compareDate(a, b) {
  const aDate = DateTime.fromISO(a.postedOn);
  const bDate = DateTime.fromISO(b.postedOn);
  if (aDate < bDate) {
    return -1;
  }
  if (aDate > bDate) {
    return 1;
  }
  return 0;
}

export function transactionsSortFunction(a, b) {
  // order by date first
  const dateCompare = compareDate(a, b);
  if (dateCompare) {
    return dateCompare;
  }

  // we sort balance adjustments to the top
  const aBalanceVal = Boolean(a.coa && a.coa.type === 'BALANCE_ADJUSTMENT') | 0;
  const bBalanceVal = Boolean(b.coa && b.coa.type === 'BALANCE_ADJUSTMENT') | 0;
  const catCompare = aBalanceVal - bBalanceVal;
  if (catCompare) {
    return catCompare;
  }
  // compare ID's if nothing else works
  const va = Number(a.id) || Number(b.id) + 1;
  const vb = Number(b.id) || Number(a.id) + 1;
  return va - vb;
}

// TODO Need UNIT TESTS for this potentially data damaging functionality

// determine if this transaction has the given tag id in it
export function txnHasTag(txn, tag) {
  if (transactionsUtils.isSplitTxn(txn)) {
    const ret = txn.split.items.find((item) => item.tags && item.tags.find((rec) => rec.id === tag.id));
    return Boolean(ret);
  }
  return txn.tags && txn.tags.filter((t) => t.id === tag.id).size > 0;
}

// remove the reference to the given tag id from the transaction
export function removeTxnTag(txn, tag) {
  if (transactionsUtils.isSplitTxn(txn)) {
    return txn.setIn(
      ['split', 'items'],
      txn.split.items.map((item) =>
        item.tags
          ? item.set(
              'tags',
              item.tags.filter((t) => t.id !== tag.id),
            )
          : item,
      ),
    );
  }
  return txn.set(
    'tags',
    txn.tags.filter((t) => t.id !== tag.id),
  );
}

export function getFieldString(field, txn, longCats) {
  switch (field) {
    case 'postedOn':
    case 'date':
      return DateTime.fromISO(txn.postedOn).toLocaleString(DateTime.DATE_SHORT);
    case 'category':
      return transactionsUtils.isSplitTxn(txn)
        ? 'SPLIT'
        : chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, longCats);
    case 'account':
    case 'accountColor':
      return accountsUtils.getAccountString(txn.accountId);
    case 'amount':
      return numberUtils.formatNumber(txn.amount, accountsUtils.getCurrencyString(txn.accountId), '0,00.00');
    case 'balance':
      return numberUtils.formatNumber(txn.balance, accountsUtils.getCurrencyString(txn.accountId), '0,00.00');
    case 'tags': {
      return transactionsUtils.makeTagsString(txn.tags) || '';
    }
    case 'check': {
      return txn.check && txn.check.number;
    }
    case 'status':
      return txn.state;
    case 'notes':
      return txn.memo ? txn.memo : '';
    default:
      return txn[field] ? txn[field] : '';
  }
}
